/**
 * Image Lightbox/Slideshow Component
 * Opens a fullscreen slideshow with blur background when triggered
 */

class ImageLightbox extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.slides = [];
    this.overlay = null;
    this.boundKeyHandler = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.image-lightbox-slide'));
    this.overlay = this.querySelector('.image-lightbox-overlay');
    
    if (!this.overlay || this.slides.length === 0) return;

    // Get references to buttons
    this.closeBtn = this.querySelector('.image-lightbox-close');
    this.prevBtn = this.querySelector('.image-lightbox-prev');
    this.nextBtn = this.querySelector('.image-lightbox-next');
    this.counter = this.querySelector('.image-lightbox-counter');

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Navigation buttons
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }

    // Close on overlay background click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Touch/swipe support for mobile
    this.setupTouchEvents();
  }

  setupTouchEvents() {
    let touchStartX = 0;
    let touchEndX = 0;

    this.overlay.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    this.overlay.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        // Swipe left - next slide
        this.nextSlide();
      } else {
        // Swipe right - previous slide
        this.prevSlide();
      }
    }
  }

  open() {
    this.currentSlide = 0;
    this.showSlide(this.currentSlide);
    this.overlay.classList.add('active');
    document.body.classList.add('lightbox-open');
    
    // Add keyboard listener
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  close() {
    this.overlay.classList.remove('active');
    document.body.classList.remove('lightbox-open');
    
    // Remove keyboard listener
    document.removeEventListener('keydown', this.boundKeyHandler);
  }

  handleKeydown(e) {
    switch (e.key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowLeft':
        this.prevSlide();
        break;
      case 'ArrowRight':
        this.nextSlide();
        break;
    }
  }

  showSlide(index) {
    // Hide all slides
    this.slides.forEach(slide => slide.classList.remove('active'));
    
    // Show current slide
    if (this.slides[index]) {
      this.slides[index].classList.add('active');
    }

    // Update counter
    this.updateCounter();

    // Update navigation buttons state
    this.updateNavButtons();
  }

  prevSlide() {
    if (this.currentSlide > 0) {
      this.currentSlide--;
      this.showSlide(this.currentSlide);
    }
  }

  nextSlide() {
    if (this.currentSlide < this.slides.length - 1) {
      this.currentSlide++;
      this.showSlide(this.currentSlide);
    }
  }

  updateCounter() {
    if (this.counter) {
      this.counter.textContent = `${this.currentSlide + 1} / ${this.slides.length}`;
    }
  }

  updateNavButtons() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentSlide === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentSlide === this.slides.length - 1;
    }
  }
}

customElements.define('image-lightbox', ImageLightbox);

// Global function to open lightbox by ID
window.openImageLightbox = function(lightboxId) {
  const lightbox = document.getElementById(lightboxId);
  if (lightbox && lightbox.open) {
    lightbox.open();
  }
};
